import { getActivityTypes } from 'reducers/activity/activityTypes';
import { interpolateDistance } from 'reducers/multiSportEdit/utils/multiSportInterpolateUtils';
import { createSelector } from 'reselect';
import InterpolatingUtil from 'utils/activity/InterpolatingUtil';
import {
    LEGS_SNAPSHOT_SET_ACTIVE_LEG,
    MINIMUM_NUM_LEGS_ALLOWED,
    NUM_MILLISECONDS_PER_SECOND
} from './multiSportConstants';
import { getFirst, getLast, pairwise, shallowCompareArrays } from './utils/genericUtils';
import { createIndexData } from './utils/indexData';
import { compareLegIndexData } from './utils/legIndexData';

/** @typedef {import('./types/entity/MultiSportState').MultiSportState} MultiSportState */
/** @typedef {import('./types/dto/ActivityDataDTO').ActivityDataDTO} ActivityDataDTO */
/** @typedef {import('./types/entity/MetricsByKey').MetricsByKey} MetricsByKey */
/** @typedef {import('./types/entity/LegStatistics').LegStatistics} LegStatistics */
/** @typedef {import('./types/entity/LegIndexData').LegIndexData} LegIndexData */
/** @typedef {import('./types/entity/RootState').RootState} RootState */
/** @typedef {import('./types/dto/ActivityReconstructRequestDTO').ActivityReconstructRequestDTO} ActivityReconstructRequestDTO */
/** @typedef {import('./types/entity/IndexData').IndexData} IndexData */
/** @typedef {import('./types/entity/EditInterval').EditInterval} EditInterval */

/******************************************************
 * Utility functions
 */

export const isStartOrEnd = (timestamp, startTimestamp, endTimestamp) => ({
    isStart: timestamp - startTimestamp < NUM_MILLISECONDS_PER_SECOND,
    isEnd: endTimestamp - timestamp < NUM_MILLISECONDS_PER_SECOND
});

/**
 * @param {LegIndexData[]} legIndexData
 * @param {number} legIndex
 * @returns {[start: number, length: number]}
 */
const calculateMetricsIntervalForLegIndex = (legIndexData, legIndex) => {
    const start = legIndexData[legIndex].metricsStartIdx;
    const end = legIndexData[legIndex + 1].metricsStartIdx;

    return [start, end];
};

/**
 * @param {LegIndexData[]} legIndexData
 * @param {number} legIndex
 * @returns {[start: number, end: number]}
 */
const calculatePolylineIntervalForLegIndex = (legIndexData, legIndex) => {
    const start = legIndexData[legIndex].polylineStartIdx;
    const end = legIndexData[legIndex + 1].polylineStartIdx;

    return [start, end];
};

/**
 * @param {MetricsByKey} allMetrics
 * @param {[start: number, end: number]} metricsInterval
 * @param {[start: number, end: number]} polyLineInterval
 * @returns {MetricsByKey}
 */
export const sliceMetrics = (allMetrics, metricsInterval, polyLineInterval) => ({
    sumDistance: allMetrics.sumDistance.slice(...metricsInterval),
    sumDuration: allMetrics.sumDuration.slice(...metricsInterval),
    directTimestamp: allMetrics.directTimestamp.slice(...metricsInterval),
    directSpeed: allMetrics.directSpeed.slice(...metricsInterval),
    directPower: allMetrics.directPower.slice(...metricsInterval),
    directElevation: allMetrics.directElevation.slice(...metricsInterval),
    directHeartRate: allMetrics.directHeartRate.slice(...metricsInterval),
    polyline: allMetrics.polyline.slice(...polyLineInterval)
});

/**
 * @param {LegIndexData} leg
 * @param {LegIndexData} nextLeg
 * @param {MetricsByKey} completeMetrics
 * @param {{ [activityId: string]: ActivityDataDTO }} legDataByActivityId
 * @param {ReturnType<typeof selectGetSumDuration>} getSumDuration
 * @param {ReturnType<typeof selectGetDistance>} getDistance
 * @return {LegStatistics}
 */
export const calculateStatisticsForLeg = (
    leg,
    nextLeg,
    completeMetrics,
    legDataByActivityId,
    getSumDuration,
    getDistance
) => {
    if (!completeMetrics || !legDataByActivityId) return null;

    const startSumDuration = getSumDuration(leg);
    const endSumDuration = getSumDuration(nextLeg);
    const duration = endSumDuration - startSumDuration;

    const cumulativeDuration = endSumDuration;

    const startDistance = getDistance(leg);
    const endDistance = getDistance(nextLeg);
    const distance = endDistance - startDistance;
    const averageSpeed = distance / duration;

    const legData = legDataByActivityId[leg.activityId];
    const unitOfPoolLength_unitKey =
        legData &&
        legData.summaryDTO &&
        legData.summaryDTO.unitOfPoolLength &&
        legData.summaryDTO.unitOfPoolLength.unitKey;

    return {
        duration,
        cumulativeDuration,
        distance,
        averageSpeed,
        typeKey: leg.typeKey,
        unitOfPoolLength_unitKey
    };
};

/**
 * @param {LegIndexData[]} legIndexData
 * @param {LegIndexData[]} initialLegIndexData
 */
export const countEditedLegs = (legIndexData, initialLegIndexData) =>
    pairwise(legIndexData)
        .map(([current, next]) => {
            const idx = initialLegIndexData.findIndex((item) => item.activityId === current.activityId);
            if (idx === -1) return null; // newly added leg
            const initialLeg = [initialLegIndexData[idx].metricsStartIdx, initialLegIndexData[idx + 1].metricsStartIdx];
            const currentLeg = [current.metricsStartIdx, next.metricsStartIdx];
            const isSame = shallowCompareArrays(initialLeg, currentLeg);
            return isSame;
        })
        .filter((value) => value !== null) // drop newly added ones
        .filter((isSame) => !isSame).length;

/******************************************************
 * Selectors
 */

/** @param {RootState} state */
export const selectMultiSportState = (state) => state.multiSportActivitySummary;

/** @param {MultiSportState} state */
export const selectErrorStatus = (state) => state.attributes.errorStatus;

/** @param {MultiSportState} state */
export const selectIsParentActivity = (state) => {
    // while there is no activity data (e.g. request is still ongoing), assume the activity is a multisport parent,
    // otherwise take the actual value
    return state.attributes.activityData ? state.attributes.activityData.isMultiSportParent : true;
};

/** @param {MultiSportState} state */
export const selectActivityData = (state) => state.attributes.activityData;

/** @param {MultiSportState} state  */
export const selectHasParentActivityData = (state) => state.attributes.hasParentActivityData;

/** @param {MultiSportState} state  */
export const selectHasChildActivityData = (state) => state.attributes.hasChildActivityData;

/** @param {MultiSportState} state  */
export const selectHasMetricsData = (state) => state.attributes.hasMetricsData;

export const selectHasAllData = createSelector(
    selectHasParentActivityData,
    selectHasChildActivityData,
    selectHasMetricsData,
    (hasParentActivityData, hasChildActivityData, hasMetricsData) =>
        hasParentActivityData && hasChildActivityData && hasMetricsData
);

/**
 * Returns the currently existing number of legs.
 * Note, that legIndexData has legCount + 1 items.
 * @param {MultiSportState} state
 */
export const selectLegCount = (state) => {
    return state.legIndexData ? state.legIndexData.length - 1 : 0;
};

/**
 * @param {MultiSportState} state
 * @returns {LegStatistics[]}
 */
export const selectLegStatistics = (state) => {
    const getSumDuration = selectGetSumDuration(state);
    const getDistance = selectGetDistance(state);
    const { completeMetricsByMetricsDescriptor, legDataByActivityId, legIndexData } = state;
    const legIndexDataLength = legIndexData && legIndexData.length;
    if (!completeMetricsByMetricsDescriptor || legIndexDataLength <= 0) return null;

    /** @type {LegStatistics[]} */

    const legStatistics = [];
    for (let i = 0; i < legIndexData.length - 1; i++) {
        const leg = legIndexData[i];
        const nextLeg = legIndexData[i + 1];
        const statsForCurrentLeg = calculateStatisticsForLeg(
            leg,
            nextLeg,
            completeMetricsByMetricsDescriptor,
            legDataByActivityId,
            getSumDuration,
            getDistance
        );
        legStatistics.push(statsForCurrentLeg);
    }

    return legStatistics;
};

/** @param {MultiSportState} state */
export const selectCompleteMetrics = (state) => state.completeMetricsByMetricsDescriptor;

/** @param {MultiSportState} state */
export const selectCurrentLegIndex = (state) => state.currentLegIndex;

/** @param {MultiSportState} state */
const selectNextLegIndex = (state) => state.currentLegIndex + 1;

/** @param {MultiSportState} state */
export const selectLegIndexData = (state) => state.legIndexData;

/** @param {MultiSportState} state */
export const selectInitialLegIndexData = (state) => state.initialLegIndexData;

/** @param {MultiSportState} state */
const selectGetLegsSnapshot = (state) =>
    /** @param {string} snapshotName */
    (snapshotName) => state.legsSnapshotByName[snapshotName];

/** @param {MultiSportState} state */
export const selectInitialActivityIds = (state) => state.activityIds;

/** @param {MultiSportState} state */
export const selectDirectTimestamp = (state) => state.completeMetricsByMetricsDescriptor.directTimestamp;

/** @param {MultiSportState} state */
export const selectPolyline = (state) => state.completeMetricsByMetricsDescriptor.polyline;

/** @param {MultiSportState} state */
export const selectSumDistance = (state) => state.completeMetricsByMetricsDescriptor.sumDistance;

/** @param {MultiSportState} state */
export const selectSumDuration = (state) => state.completeMetricsByMetricsDescriptor.sumDuration;

/** @param {MultiSportState} state */
export const selectIsSubmittingReconstructedActivity = (state) => state.putReconstructedActivityAttributes.isSubmitting;

/** @param {MultiSportState} state */
export const selectShowAlert = (state) => state.putReconstructedActivityAttributes.showAlert;

/** @param {MultiSportState} state */
export const selectLegDataByActivityId = (state) => state.legDataByActivityId;

/** @param {MultiSportState} state */
export const selectActivityStartTimestamp = (state) => state.completeMetricsByMetricsDescriptor.directTimestamp[0];

/** @param {MultiSportState} state */
export const selectActivityStartSumDuration = (state) => state.completeMetricsByMetricsDescriptor.sumDuration[0];

/** @param {MultiSportState} state */
export const selectActivityEndTimestamp = (state) => getLast(state.completeMetricsByMetricsDescriptor.directTimestamp);

/** @param {MultiSportState} state */
export const selectActivityEndSumDuration = (state) => getLast(state.completeMetricsByMetricsDescriptor.sumDuration);

export const selectActivityEndTime = createSelector(
    selectActivityStartTimestamp,
    selectActivityEndTimestamp,
    (activityStartTimestamp, activityEndTimestamp) => {
        const activityEndTimeSeconds = (activityEndTimestamp - activityStartTimestamp) / NUM_MILLISECONDS_PER_SECOND;
        return activityEndTimeSeconds;
    }
);

/** @param {MultiSportState} state */
export const selectActivityStartDistance = (state) => state.completeMetricsByMetricsDescriptor.sumDistance[0];

/** @param {MultiSportState} state */
export const selectActivityEndDistance = (state) => getLast(state.completeMetricsByMetricsDescriptor.sumDistance);

/** @param {MultiSportState} state */
export const selectActivityEndMetricsIdx = (state) =>
    state.completeMetricsByMetricsDescriptor.directTimestamp.length - 1;

/** @param {MultiSportState} state */
export const selectHasInvalidStartEndTime = (state) => state.attributes.hasInvalidStartEndTime;

export const selectCurrentLegIndexData = createSelector(
    selectCurrentLegIndex,
    selectLegIndexData,
    (currentLegIndex, legIndexData) => (legIndexData ? legIndexData[currentLegIndex] : null)
);

export const selectNextLegIndexData = createSelector(
    selectNextLegIndex,
    selectLegIndexData,
    (nextLegIndex, legIndexData) => (legIndexData ? legIndexData[nextLegIndex] : null)
);

const selectNextNextLegIndexData = createSelector(
    selectCurrentLegIndex,
    selectLegIndexData,
    (currentLegIndex, legIndexData) => (legIndexData ? legIndexData[currentLegIndex + 2] : null)
);

export const selectIsLastLeg = createSelector(
    selectCurrentLegIndex,
    selectLegCount,
    (currentLegIndex, legCount) => currentLegIndex === legCount - 1
);

const selectEditStart = createSelector(
    selectCurrentLegIndexData,
    (currentLegIndexData) => currentLegIndexData
);

const selectEditMiddle = createSelector(
    selectCurrentLegIndex,
    selectLegIndexData,
    (currentLegIndex, legIndexData) => legIndexData[currentLegIndex + 1]
);

const selectEditEnd = createSelector(
    selectNextNextLegIndexData,
    selectInitialLegIndexData,
    selectIsLastLeg,
    (nextNextLegIndexData, initialLegIndexData, isLastLeg) =>
        isLastLeg
            ? // current leg is last leg => end has to point ALWAYS to last item of complete metrics
              initialLegIndexData[initialLegIndexData.length - 1]
            : // current leg is aheadof last leg
              nextNextLegIndexData
);

export const selectEditingStartEnd = createSelector(
    selectEditStart,
    selectEditEnd,
    (start, end) => ({ start, end })
);

export const selectEditInterval = createSelector(
    selectEditStart,
    selectEditMiddle,
    selectEditEnd,
    (start, middle, end) => /**@type {EditInterval} */ ({
        start,
        middle,
        end
    })
);

// filtering examples:  src/Reports/ActivitySelector.jsx  and src/utils/ActivityTypesUtil.js
export const selectTrimmableActivityTypes = createSelector(
    getActivityTypes,
    (activityTypeDTOs) => {
        const result = activityTypeDTOs.filter(
            (activityTypeDTO) =>
                activityTypeDTO.trimmable && !activityTypeDTO.restricted && activityTypeDTO.typeKey !== 'multi_sport'
        );
        return result;
    }
);

/** creates an IndexData to sumDuration converter */
export const selectGetSumDuration = createSelector(
    selectDirectTimestamp,
    selectSumDuration,
    (directTimestamp, sumDuration) => {
        /** @param {IndexData} indexData */
        const getSumDuration = ({ metricsStartIdx, timestamp }) => {
            let resultDuration;
            if (!timestamp) {
                resultDuration = sumDuration[metricsStartIdx];
            } else {
                // interpolate sumDuration from timestamp
                const a = directTimestamp[metricsStartIdx - 1],
                    a1 = directTimestamp[metricsStartIdx];
                const b = sumDuration[metricsStartIdx - 1],
                    b1 = sumDuration[metricsStartIdx];
                resultDuration = InterpolatingUtil.linearInterpolating(a, b, a1, b1, timestamp);
            }
            return resultDuration;
        };

        return getSumDuration;
    }
);

export const selectTotalDuration = createSelector(
    selectLegIndexData,
    selectGetSumDuration,
    (legIndexData, getSumDuration) => {
        const startSumDuration = getSumDuration(getFirst(legIndexData));
        const endSumDuration = getSumDuration(getLast(legIndexData));

        return endSumDuration - startSumDuration;
    }
);

export const selectCurrentLegTypeKey = createSelector(
    selectCurrentLegIndexData,
    (currentLegIndexData) => currentLegIndexData && currentLegIndexData.typeKey
);

export const selectNextLegTypeKey = createSelector(
    selectNextLegIndexData,
    (nextLegIndexData) => nextLegIndexData && nextLegIndexData.typeKey
);

export const selectCurrentLegMetricsData = createSelector(
    selectCurrentLegIndex,
    selectLegIndexData,
    selectCompleteMetrics,
    (currentLegIndex, legIndexData, completeMetrics) => {
        if (!completeMetrics) return null;

        const metricsInterval = calculateMetricsIntervalForLegIndex(legIndexData, currentLegIndex);
        const polylineInterval = calculatePolylineIntervalForLegIndex(legIndexData, currentLegIndex);

        return sliceMetrics(completeMetrics, metricsInterval, polylineInterval);
    }
);

export const selectNextLegMetricsData = createSelector(
    selectNextLegIndex,
    selectIsLastLeg,
    selectLegIndexData,
    selectCompleteMetrics,
    (nextLegIndex, isLastLeg, legIndexData, completeMetrics) => {
        if (isLastLeg) return null;
        if (!completeMetrics) return null;

        const metricsInterval = calculateMetricsIntervalForLegIndex(legIndexData, nextLegIndex);
        const polylineInterval = calculatePolylineIntervalForLegIndex(legIndexData, nextLegIndex);

        return sliceMetrics(completeMetrics, metricsInterval, polylineInterval);
    }
);

const selectCurrentLegActivityId = createSelector(
    selectCurrentLegIndex,
    selectLegIndexData,
    (currentLegIndex, legIndexData) => {
        const legIndexDatum = legIndexData && legIndexData[currentLegIndex];
        const activityId = legIndexDatum && legIndexDatum.activityId;
        return activityId;
    }
);

const selectNextLegActivityId = createSelector(
    selectNextLegIndex,
    selectLegIndexData,
    (currentLegIndex, legIndexData) => {
        const legIndexDatum = legIndexData && legIndexData[currentLegIndex];
        const activityId = legIndexDatum && legIndexDatum.activityId;
        return activityId;
    }
);

export const selectCurrentLegUnitOfPoolLength = createSelector(
    selectLegDataByActivityId,
    selectCurrentLegActivityId,
    (legDataByActivityId, currentLegActivityId) => {
        const activityDataDTO = currentLegActivityId && legDataByActivityId[currentLegActivityId];
        const unitOfPoolLength = activityDataDTO && activityDataDTO.summaryDTO.unitOfPoolLength;
        return unitOfPoolLength;
    }
);

export const selectNextLegUnitOfPoolLength = createSelector(
    selectLegDataByActivityId,
    selectNextLegActivityId,
    (legDataByActivityId, nextLegActivityId) => {
        const activityDataDTO = nextLegActivityId && legDataByActivityId[nextLegActivityId];
        const unitOfPoolLength = activityDataDTO && activityDataDTO.summaryDTO.unitOfPoolLength;
        return unitOfPoolLength;
    }
);

export const selectIsDeleteLegOperationAllowed = createSelector(
    selectLegCount,
    (legCount) => legCount > MINIMUM_NUM_LEGS_ALLOWED
);

export const OPERATION_ADD = 'ADD';
export const OPERATION_EDIT = 'EDIT';
export const OPERATION_DELETE = 'DELETE';

export const selectAssembleReconstructDTOs = createSelector(
    selectInitialLegIndexData,
    selectDirectTimestamp,
    (initialLegIndexData, directTimestamp) =>
        /**
         * @param {LegIndexData[]} legIndexData
         */
        (legIndexData) => {
            const currentIds = legIndexData.map((item) => item.activityId);
            const getOperation = (activityId) => (activityId < 0 ? OPERATION_ADD : OPERATION_EDIT); // newly added legs have negative indexes

            /** @type {ActivityReconstructRequestDTO[]} */
            const reconstructDTOsForAddEdit = pairwise(legIndexData).map(
                ([{ activityId, typeId, timestamp, metricsStartIdx }, next]) => ({
                    activityId,
                    activityTypeDTO: { typeId },
                    operation: getOperation(activityId),
                    startTime: timestamp || directTimestamp[metricsStartIdx],
                    endTime: next.timestamp || directTimestamp[next.metricsStartIdx]
                })
            );

            /** @type {ActivityReconstructRequestDTO[]} */
            const reconstructDTOsForDelete = initialLegIndexData
                .filter(({ activityId }) => !currentIds.includes(activityId))
                .map((item) => ({
                    activityId: item.activityId,
                    operation: OPERATION_DELETE
                }));

            const reconstructDTOs = reconstructDTOsForAddEdit.concat(reconstructDTOsForDelete);
            return reconstructDTOs;
        }
);

export const selectReconstructDTOs = createSelector(
    selectLegIndexData,
    selectInitialLegIndexData,
    selectAssembleReconstructDTOs,
    (legIndexData, initialLegIndexData, assembleReconstructDTOs) => {
        const reconstructDTOs = assembleReconstructDTOs(legIndexData);
        const addedLegsCount = reconstructDTOs.filter((leg) => leg.operation === OPERATION_ADD).length;
        const deletedLegsCount = reconstructDTOs.filter((leg) => leg.operation === OPERATION_DELETE).length;
        const editedLegsCount = countEditedLegs(legIndexData, initialLegIndexData);

        let convertAnalyticsString = '';
        legIndexData.forEach((leg, index) => {
            if (!leg.typeKey) return;
            if (index === 0) {
                convertAnalyticsString += leg.typeKey += ' to ' + leg.typeKey + ', ';
                return;
            }
            convertAnalyticsString += leg.typeKey;
        });

        return {
            reconstructDTOs,
            convertAnalyticsString,
            analyticsData: {
                addedLegsCount,
                deletedLegsCount,
                editedLegsCount
            }
        };
    }
);

export const selectIsCurrentLegResettable = createSelector(
    selectLegIndexData,
    selectGetLegsSnapshot,
    (legIndexData, getLegIndexDataSnapshot) => {
        const snapshot = getLegIndexDataSnapshot(LEGS_SNAPSHOT_SET_ACTIVE_LEG);
        // snapshot must exist
        if (!snapshot) {
            return false;
        }

        // snapshot should differ
        const areEqual = shallowCompareArrays(legIndexData, snapshot);
        return !areEqual;
    }
);

export const selectIsActivityModified = createSelector(
    selectLegIndexData,
    selectInitialLegIndexData,
    (legIndexData, initialLegIndexData) => {
        const areEqual = shallowCompareArrays(legIndexData, initialLegIndexData, compareLegIndexData);
        return !areEqual;
    }
);

/** creates an IndexData to timestamp converter */
export const selectGetTimestamp = createSelector(
    selectDirectTimestamp,
    (directTimestamp) => {
        /** @param {IndexData} indexData */
        const getTimestamp = (indexData) => indexData.timestamp || directTimestamp[indexData.metricsStartIdx];

        return getTimestamp;
    }
);

/** creates an IndexData to distance converter */
export const selectGetDistance = createSelector(
    selectDirectTimestamp,
    selectSumDistance,
    (directTimestamp, sumDistance) => {
        /** @param {IndexData} indexData */
        const getDistance = ({ metricsStartIdx, timestamp }) =>
            timestamp
                ? interpolateDistance(timestamp, directTimestamp, sumDistance, metricsStartIdx)
                : sumDistance[metricsStartIdx];

        return getDistance;
    }
);

export const selectCurrentLegStatistics = createSelector(
    selectCurrentLegIndexData,
    selectNextLegIndexData,
    selectCompleteMetrics,
    selectLegDataByActivityId,
    selectGetSumDuration,
    selectGetDistance,
    calculateStatisticsForLeg
);

const selectFindClosestIndexDataWithSameSumDurationBackwards = createSelector(
    selectSumDuration,
    (sumDuration) =>
        /**
         * goes backwards with index in sumDuration until sumDuration[i] is the same
         * @param {IndexData} indexData
         */
        (indexData) => {
            const currentSumDuration = sumDuration[indexData.metricsStartIdx];
            let newMetricsStartIdx = indexData.metricsStartIdx;
            while (newMetricsStartIdx >= 0 && sumDuration[newMetricsStartIdx] === currentSumDuration)
                newMetricsStartIdx--;
            newMetricsStartIdx++;

            return newMetricsStartIdx === indexData.metricsStartIdx ? indexData : createIndexData(newMetricsStartIdx);
        }
);

export const selectCheckStartEnd = createSelector(
    selectGetTimestamp,
    selectFindClosestIndexDataWithSameSumDurationBackwards,
    (getTimestamp, findClosestIndexDataWithSameSumDurationBackwards) =>
        /**
         * Checks whether timestamp is at the start or end of metricsInterval
         * @param {EditInterval} metricsInterval
         * @param {number} timestamp
         */
        (metricsInterval, timestamp) => {
            const end = findClosestIndexDataWithSameSumDurationBackwards(metricsInterval.end);
            const startTimestamp = getTimestamp(metricsInterval.start);
            const endTimestamp = getTimestamp(end);

            const result = isStartOrEnd(timestamp, startTimestamp, endTimestamp);
            return result;
        }
);
